import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { buildRoute, ROUTES } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';
import { PackagesShimmer } from '../components/shimmer';
import { edosApi, useGetPackagesQuery } from '../store/edosApi';
import { byName, FAMILY_ORDER } from '../data/packageFamilies';
import { splitParameters } from '../utils/mapApiDetail';
import heroImg from '../assets/img/v5/hero.jpg';

// Packages — built to the v5 reference.
//
// The catalogue is live (63 packages from eDOS) but has no family field:
// `departments` reads "Package" on every row. The reference groups the
// same catalogue into patient-facing families and body systems, so those
// two things come from src/data/packageFamilies.js and are joined onto
// each live row by name. Everything else — id, turnaround, the real
// parameter list, the detail link — is the API's.
//
// One departure from the reference: it prints "₹ [price]" on every card.
// The packages endpoint returns `mrp: null` and `l2l_price: null` for all
// 63, so there is no price to print and the row is left out rather than
// showing a zero. The reference's own layout allows for this — its card
// takes a `showPrice` flag.

// First card of a family is dark, the rest are light. The reference uses
// this to break up what would otherwise be a wall of white cards.
const THEMES = {
  light: {
    bg: '#FFFFFF', fg: 'var(--tl-g9)', tagBg: 'var(--tl-mint)', tagFg: 'var(--tl-teal-d)',
    chipBg: 'var(--tl-mint)', btnBg: 'var(--tl-g9)', btnFg: '#FFFFFF',
  },
  dark: {
    bg: 'var(--tl-g9)', fg: '#FFFFFF', tagBg: 'var(--tl-yellow)', tagFg: 'var(--tl-g9)',
    chipBg: 'var(--tl-g7)', btnBg: 'var(--tl-teal)', btnFg: '#FFFFFF',
  },
};

// Loads every page of the catalogue into the RTK cache, then flattens it.
// The family filter and its counts are client-side, so the whole list has
// to be present before the chips can be honest about how many each holds.
const useAllPackages = () => {
  const dispatch = useDispatch();
  const { data: page1, isLoading: p1Loading } = useGetPackagesQuery({ page: 1 });
  const [restItems, setRestItems] = useState([]);
  const [fetchingRest, setFetchingRest] = useState(false);

  useEffect(() => {
    if (!page1?.items) return;
    const lastPage = page1.pagination?.last_page || 1;
    if (lastPage <= 1) { setRestItems([]); return; }
    setFetchingRest(true);
    const requests = [];
    for (let p = 2; p <= lastPage; p += 1) {
      requests.push(dispatch(edosApi.endpoints.getPackages.initiate({ page: p })).unwrap());
    }
    Promise.all(requests)
      .then((pages) => setRestItems(pages.flatMap((x) => x?.items || [])))
      .catch(() => setRestItems([]))
      .finally(() => setFetchingRest(false));
  }, [page1, dispatch]);

  // Dedupe by id — eDOS occasionally repeats a package across pages.
  const items = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of [...(page1?.items || []), ...restItems]) {
      if (!p || p.id == null || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push(p);
    }
    return out;
  }, [page1, restItems]);

  return { items, isLoading: p1Loading || fetchingRest };
};

const Packages = () => {
  const { items, isLoading } = useAllPackages();
  const [family, setFamily] = useState('All');
  const [open, setOpen] = useState({});

  // Join each live row to its curated family + body systems. Anything the
  // catalogue adds later that this file does not know about still shows,
  // under "Other", rather than vanishing from the grid.
  const packages = useMemo(() => items.map((row) => {
    const meta = byName(row.test_name);
    const sys = meta?.sys || [];
    return {
      id: row.id,
      name: row.test_name,
      family: meta?.f || 'Other',
      blurb: meta?.q || row.clinical_aid || '',
      params: meta?.par || splitParameters(row.parameters).length,
      profiles: sys.reduce((t, g) => t + g.p.length, 0),
      systems: sys.length,
      sys,
      one: meta?.one || '',
      tat: row.report_tat || '',
    };
  }), [items]);

  // Families in the reference's order, then anything unexpected, each
  // with the count the live catalogue actually holds.
  const families = useMemo(() => {
    const counts = packages.reduce((m, p) => {
      m[p.family] = (m[p.family] || 0) + 1;
      return m;
    }, {});
    const ordered = FAMILY_ORDER.filter((f) => counts[f]);
    const extra = Object.keys(counts).filter((f) => !ordered.includes(f)).sort();
    return [
      { label: 'All', n: packages.length },
      ...[...ordered, ...extra].map((f) => ({ label: f, n: counts[f] })),
    ];
  }, [packages]);

  const shown = family === 'All' ? packages : packages.filter((p) => p.family === family);

  // Reset which cards are expanded when the filter changes, so a card
  // does not open at a position the user never clicked.
  useEffect(() => { setOpen({}); }, [family]);

  const familyCount = families.length - 1;

  return (
    <Box>
      {/* ── 1. hero ──────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            minHeight: { xs: 280, md: 360 },
            display: 'flex',
            alignItems: 'end',
            borderRadius: '36px',
            overflow: 'hidden',
            bgcolor: 'var(--tl-g9)',
          }}
        >
          <Box
            component="img"
            src={heroImg}
            alt=""
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {/* Left-weighted scrim so the headline keeps contrast over any
              part of the photograph. */}
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(90deg, rgba(7,33,29,0.92) 0%, rgba(7,33,29,0.7) 50%, rgba(7,33,29,0.2) 100%)',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: 2.25,
              p: 'clamp(28px, 5vw, 56px)',
              color: '#FFFFFF',
            }}
          >
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(40px, 6vw, 72px)',
                lineHeight: 0.95,
                letterSpacing: '-.035em',
                maxWidth: 760,
                textWrap: 'balance',
                color: '#FFFFFF',
              }}
            >
              One visit. <Box component="span" sx={{ color: 'var(--tl-teal-l)' }}>The full picture.</Box>
            </Typography>
            <Typography
              sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: '#D5E7E2', maxWidth: 540 }}
            >
              Every package needs a single home visit, and all results arrive together,
              reviewed by a doctor.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── 2. browse packages ───────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 1.5, px: 2 }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.75,
            p: 'clamp(22px, 4vw, 40px)',
            borderRadius: '36px',
            bgcolor: 'var(--tl-g9)',
            color: '#FFFFFF',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'end',
              justifyContent: 'space-between',
              gap: '12px 28px',
            }}
          >
            <Typography
              component="h2"
              sx={{
                m: 0,
                color: '#FFFFFF',
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(28px, 3.4vw, 40px)',
                lineHeight: 1,
                letterSpacing: '-.03em',
              }}
            >
              Browse packages
            </Typography>
            <Box
              component="span"
              sx={{ fontFamily: UI, fontSize: 15, fontWeight: 700, color: 'var(--tl-teal-l)' }}
            >
              {isLoading
                ? 'Loading…'
                : `${packages.length} package${packages.length === 1 ? '' : 's'} · ${familyCount} famil${familyCount === 1 ? 'y' : 'ies'}`}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {families.map((f) => {
              const on = f.label === family;
              return (
                <Box
                  key={f.label}
                  component="button"
                  type="button"
                  onClick={() => setFamily(f.label)}
                  aria-pressed={on}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    borderRadius: '999px',
                    p: '11px 18px',
                    whiteSpace: 'nowrap',
                    fontFamily: UI,
                    fontSize: 15,
                    fontWeight: 700,
                    transition: `background-color .25s ${EASE}, border-color .25s ${EASE}`,
                    border: `1.5px solid ${on ? 'var(--tl-teal-l)' : '#3A5F58'}`,
                    bgcolor: on ? 'var(--tl-teal-l)' : 'transparent',
                    color: on ? 'var(--tl-g9)' : '#FFFFFF',
                    '&:hover': { borderColor: 'var(--tl-teal-l)' },
                  }}
                >
                  {f.label}
                  <Box component="span" sx={{ fontSize: 12, fontWeight: 800, opacity: 0.65 }}>
                    {f.n}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ── 3. results header ────────────────────────────────────────── */}
      <Box
        component="section"
        sx={{
          width: '100%',
          maxWidth: 1320,
          mx: 'auto',
          px: 2,
          pt: 4.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'end',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography
          component="h2"
          sx={{
            m: 0,
            px: 1,
            fontFamily: DISPLAY,
            fontWeight: 700,
            fontSize: 'clamp(28px, 3.6vw, 44px)',
            lineHeight: 1,
            letterSpacing: '-.03em',
            color: 'var(--tl-g9)',
          }}
        >
          {family === 'All' ? 'All packages' : family}
        </Typography>
        <Box component="span" sx={{ px: 1, fontFamily: UI, fontSize: 15, fontWeight: 700, color: 'var(--tl-g9)' }}>
          {`${shown.length} package${shown.length === 1 ? '' : 's'}`}
        </Box>
      </Box>

      {/* ── 4. the grid ──────────────────────────────────────────────── */}
      <Box component="section" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 2 }}>
        {isLoading ? (
          <PackagesShimmer count={6} min={340} />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
              gap: 1.5,
              alignItems: 'start',
            }}
          >
            {(() => {
              // Tracks the first card of each family in render order, which
              // is what decides the dark treatment.
              const seen = {};
              return shown.map((p) => {
                const first = !seen[p.family];
                seen[p.family] = true;
                const th = THEMES[first ? 'dark' : 'light'];
                const isOpen = !!open[p.id];
                return (
                  <Box
                    key={p.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      p: '28px',
                      borderRadius: '32px',
                      bgcolor: th.bg,
                      color: th.fg,
                      ...(first ? null : { border: '1.5px solid var(--tl-line)' }),
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        component="span"
                        sx={{
                          px: '10px',
                          py: '6px',
                          borderRadius: '999px',
                          bgcolor: th.tagBg,
                          color: th.tagFg,
                          fontFamily: UI,
                          fontSize: 12,
                          fontWeight: 800,
                        }}
                      >
                        {p.family}
                      </Box>
                      {p.tat && (
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, opacity: 0.75 }}>
                          {p.tat}
                        </Box>
                      )}
                    </Box>

                    <Box
                      component={Link}
                      to={buildRoute(ROUTES.PACKAGE_DETAIL, { id: p.id })}
                      sx={{
                        fontFamily: DISPLAY,
                        fontWeight: 700,
                        fontSize: 28,
                        lineHeight: 1.05,
                        letterSpacing: '-.02em',
                        color: th.fg,
                        textDecoration: 'none',
                        '&:hover': { color: th.fg, textDecoration: 'underline' },
                      }}
                    >
                      {p.name}
                    </Box>

                    {p.blurb && (
                      <Box
                        component="span"
                        sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, opacity: 0.85, textWrap: 'pretty' }}
                      >
                        {p.blurb}
                      </Box>
                    )}

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.75 }}>
                      {[
                        { v: p.params, l: 'parameters' },
                        { v: p.profiles, l: 'profiles' },
                        { v: p.systems, l: 'body systems' },
                      ].map((s) => (
                        <Box
                          key={s.l}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            p: '12px 10px',
                            borderRadius: '16px',
                            bgcolor: th.chipBg,
                          }}
                        >
                          <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 26, lineHeight: 1 }}>
                            {s.v}
                          </Box>
                          <Box component="span" sx={{ fontFamily: UI, fontSize: 12, fontWeight: 800, opacity: 0.8 }}>
                            {s.l}
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    {p.sys.length > 0 && (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setOpen((o) => ({ ...o, [p.id]: !o[p.id] }))}
                        aria-expanded={isOpen}
                        sx={{
                          alignSelf: 'start',
                          cursor: 'pointer',
                          border: 0,
                          borderRadius: '999px',
                          p: '9px 14px',
                          bgcolor: th.chipBg,
                          color: th.fg,
                          fontFamily: UI,
                          fontSize: 14,
                          fontWeight: 800,
                        }}
                      >
                        {isOpen ? "Hide what's included −" : "See what's included +"}
                      </Box>
                    )}

                    {isOpen && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                        {p.sys.map((g) => (
                          <Box key={g.s} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            <Box
                              component="span"
                              sx={{
                                fontFamily: UI,
                                fontSize: 12,
                                fontWeight: 800,
                                letterSpacing: '.06em',
                                textTransform: 'uppercase',
                                opacity: 0.7,
                              }}
                            >
                              {g.s}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {g.p.map((x) => (
                                <Box
                                  key={x}
                                  component="span"
                                  sx={{
                                    p: '7px 12px',
                                    borderRadius: '999px',
                                    bgcolor: th.chipBg,
                                    fontFamily: UI,
                                    fontSize: 13,
                                    fontWeight: 700,
                                  }}
                                >
                                  {x}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ))}
                        {p.one && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            <Box
                              component="span"
                              sx={{
                                fontFamily: UI,
                                fontSize: 12,
                                fontWeight: 800,
                                letterSpacing: '.06em',
                                textTransform: 'uppercase',
                                opacity: 0.7,
                              }}
                            >
                              Single tests
                            </Box>
                            <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, lineHeight: 1.5 }}>
                              {p.one}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 1.5,
                        pt: 0.75,
                      }}
                    >
                      <Box
                        component={Link}
                        to={buildRoute(ROUTES.PACKAGE_DETAIL, { id: p.id })}
                        sx={{
                          p: '13px 18px',
                          borderRadius: '14px',
                          bgcolor: th.btnBg,
                          color: th.btnFg,
                          fontFamily: UI,
                          fontSize: 15,
                          fontWeight: 800,
                          textDecoration: 'none',
                          whiteSpace: 'nowrap',
                          transition: `filter .25s ${EASE}`,
                          '&:hover': { color: th.btnFg, filter: 'brightness(1.08)' },
                        }}
                      >
                        Book package
                      </Box>
                    </Box>
                  </Box>
                );
              });
            })()}
          </Box>
        )}
      </Box>

      {/* ── 5. corporate band ────────────────────────────────────────── */}
      <Box component="section" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            p: 'clamp(24px, 4vw, 44px)',
            borderRadius: '32px',
            bgcolor: 'var(--tl-yellow)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 620 }}>
            <Box
              component="span"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(28px, 3.2vw, 40px)',
                letterSpacing: '-.02em',
                lineHeight: 1.05,
                color: 'var(--tl-g9)',
              }}
            >
              Looking after a team?
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.55, color: '#3A4020' }}>
              On-site and at-home health checks for companies. Tell us about your team and
              we&rsquo;ll build a package.
            </Box>
          </Box>
          <Box
            component={Link}
            to={ROUTES.CORPORATE}
            sx={{
              p: '15px 22px',
              borderRadius: '999px',
              bgcolor: 'var(--tl-g9)',
              color: '#FFFFFF',
              fontFamily: UI,
              fontSize: 16,
              fontWeight: 800,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: 'var(--tl-g8)', color: '#FFFFFF' },
            }}
          >
            Talk to us
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Packages;
